import React, { useState, useRef } from 'react';
import { Upload, Download, Play, RotateCcw, Plus, Trash2, Box, ListChecks, FileImage, Sun, Check, ChevronLeft, ChevronDown, X, Lock, Edit2, ArrowLeftRight, ArrowUpDown, Circle, Info, Smartphone, Palette, Printer, AlertTriangle, Save, FolderOpen, Sparkles } from 'lucide-react';
import { useCutList, ALGORITHM_INFO } from '../contexts/CutListContext';
import { useAuth, FEATURES } from '../contexts/AuthContext';
import { useOptimizer } from '../hooks/useOptimizer';
import SheetRenderer, { SheetCard } from '../shared/SheetRenderer';
import { getPartColor } from '../utils/colors';
import UnitSelector from '../components/UnitSelector';
import CSVColumnMapper from '../components/CSVColumnMapper';
import Model3DImporter from '../components/Model3DImporter';
import { uid } from '../utils/uid';
import { useWakeLock } from '../hooks/useWakeLock';
import { parseValue, formatDimension, formatPreciseDimension, getCSVUnitLabel, roundForCSV, UNIT_SYSTEMS, getUnitDefaults, getThicknessTolerance } from '../utils/units';
import { orderCutsForFabrication } from '../utils/cutOrderOptimizer';
import { downloadShopSheet, downloadShopSheetPDF } from '../utils/printShopSheet';
import { exportCutcalcFile, readCutcalcFile, validateProject } from '../utils/cutcalcFile';
import { recolorSVG } from '../utils/modelToSVG';
import iconImg from '../assets/newicon.png';
import ConfirmDialog from '../shared/ConfirmDialog';

/**
 * Desktop App - "The Planner"
 * Three-pane layout: Left (Inputs), Center (Controls/Stats), Right (Visualization)
 * Designed for sitting at a desk, designing, importing data, and optimizing material usage
 */
const DesktopApp = ({ onShowAbout, onSwitchToMobile }) => {
  const {
    pieces,
    setPieces,
    addPiece,
    updatePiece,
    removePiece,
    addPieces,
    sheetWidth,
    setSheetWidth,
    sheetLength,
    setSheetLength,
    cutWidth,
    setCutWidth,
    trimFactoryEdges,
    setTrimFactoryEdges,
    trimAmount,
    setTrimAmount,
    currentUnits,
    setCurrentUnits,
    handleUnitChange,
    algorithm,
    setAlgorithm,
    layouts,
    setLayouts,
    stats,
    setStats,
    errors,
    addError,
    resetAll,
    highlightedPieceId,
    setHighlightedPieceId,
    // Stock sheet types
    stockSheetTypes,
    setStockSheetTypes,
    addStockSheetType,
    updateStockSheetType,
    removeStockSheetType,
  } = useCutList();

  // Auth for feature gating
  const {
    isPro,
    license,
    promptUpgrade,
    canUseAlgorithm,
    hasFeature,
    checkImportAllowed,
  } = useAuth();
  const showWatermark = !hasFeature(FEATURES.CLEAN_EXPORT);

  const { optimize } = useOptimizer();
  const fileInputRef = useRef(null);
  const projectFileInputRef = useRef(null);

  // CSV Column Mapper state
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [isDragging, setIsDragging] = useState(false);

  // 3D Model Importer state
  const [show3DImporter, setShow3DImporter] = useState(false);
  const [pending3DFile, setPending3DFile] = useState(null);
  const [modelSnapshot, setModelSnapshot] = useState(null);

  // Active tab for input pane
  const [activeInputTab, setActiveInputTab] = useState('manual');
  const [algorithmDropdownOpen, setAlgorithmDropdownOpen] = useState(false);

  // Execution mode state
  const [executionMode, setExecutionMode] = useState(null); // null | 'guided' | 'shopsheet'
  const [currentSheetIndex, setCurrentSheetIndex] = useState(0);
  const [currentPieceIndex, setCurrentPieceIndex] = useState(0);
  const [completedPieces, setCompletedPieces] = useState(new Set());

  // Preview modal state for free users
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Wake lock for shop sheet mode
  const { isLocked, isSupported, toggleWakeLock } = useWakeLock();

  // Stock sheet type modal state
  const [showStockTypeModal, setShowStockTypeModal] = useState(false);
  const [editingStockType, setEditingStockType] = useState(null);
  const [stockTypeForm, setStockTypeForm] = useState({
    name: '',
    thickness: '',
    width: '',
    length: ''
  });

  // Selected stock type for adding pieces
  const [selectedStockTypeId, setSelectedStockTypeId] = useState(null);

  // Add piece modal state
  const [showAddPieceModal, setShowAddPieceModal] = useState(false);
  const LAST_PIECE_PARAMS_KEY = 'cutcalc-last-piece-params';

  // Load last used piece parameters from localStorage (width and length only)
  const getLastPieceParams = () => {
    try {
      const saved = localStorage.getItem(LAST_PIECE_PARAMS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load last piece params:', e);
    }
    // Default values (no thickness - that comes from the sheet)
    return {
      width: currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 8 : 20,
      length: currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 12 : 30,
    };
  };

  // Normalize lastParams: migrate stale localStorage with old 'height' key
  const normalizeLastParams = (params) => {
    const w = params.width ?? (currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 8 : 20);
    const l = params.length ?? params.height ?? (currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 12 : 30);
    return { width: w, length: l };
  };

  const [pieceForm, setPieceForm] = useState({
    name: '',
    width: '',
    length: '',
    quantity: '1',
    grainDirection: 'none',
    stockSheetTypeId: null,
  });

  // Open add piece modal
  const openAddPieceModal = () => {
    const lastParams = normalizeLastParams(getLastPieceParams());

    // Auto-select grain direction based on remembered dimensions
    const pieceW = lastParams.width;
    const pieceL = lastParams.length;
    // Get sheet dimensions inline since helper may not be defined yet
    let sheetW = sheetWidth, sheetL = sheetLength;
    if (selectedStockTypeId) {
      const sheet = stockSheetTypes.find(t => t.id === selectedStockTypeId);
      if (sheet) { sheetW = sheet.width; sheetL = sheet.length; }
    }
    // Calculate auto grain inline
    const fitsAlongLength = pieceL <= sheetL && pieceW <= sheetW;
    const fitsAlongWidth = pieceW <= sheetL && pieceL <= sheetW;
    let autoGrain = 'none';
    if (!(fitsAlongLength && fitsAlongWidth)) {
      if (fitsAlongLength) autoGrain = 'horizontal';
      else if (fitsAlongWidth) autoGrain = 'vertical';
    }

    setPieceForm({
      name: `Part ${pieces.length + 1}`,
      width: lastParams.width.toString(),
      length: lastParams.length.toString(),
      quantity: '1',
      grainDirection: autoGrain,
      stockSheetTypeId: selectedStockTypeId,
    });
    setShowAddPieceModal(true);
  };

  // Get thickness from a sheet or default
  const getThicknessForSheet = (stockTypeId) => {
    if (stockTypeId) {
      const sheet = stockSheetTypes.find(t => t.id === stockTypeId);
      if (sheet) return sheet.thickness;
    }
    return getUnitDefaults(currentUnits.unit, currentUnits.system).thickness;
  };

  // Get sheet dimensions for a stock type (or default)
  const getSheetDimensionsForStockType = (stockTypeId) => {
    if (stockTypeId) {
      const sheet = stockSheetTypes.find(t => t.id === stockTypeId);
      if (sheet) return { width: sheet.width, length: sheet.length };
    }
    return { width: sheetWidth, length: sheetLength };
  };

  // Check if a grain direction is valid for given piece and sheet dimensions
  // 'horizontal' = Along Length: piece length along sheet length
  // 'vertical' = Along Width: piece width along sheet length
  const isGrainDirectionValid = (pieceW, pieceL, grain, sheetW, sheetL) => {
    const fitsAlongLength = pieceL <= sheetL && pieceW <= sheetW;
    const fitsAlongWidth = pieceW <= sheetL && pieceL <= sheetW;

    if (grain === 'none') {
      return fitsAlongLength && fitsAlongWidth;
    }
    if (grain === 'horizontal') return fitsAlongLength;
    if (grain === 'vertical') return fitsAlongWidth;
    return true;
  };

  // State for grain direction warning
  const [grainWarning, setGrainWarning] = useState(null);

  // Auto-select the best valid grain direction for given dimensions
  const getAutoGrainDirection = (pieceW, pieceL, sheetW, sheetL) => {
    const noneValid = isGrainDirectionValid(pieceW, pieceL, 'none', sheetW, sheetL);
    if (noneValid) return 'none';
    const horizValid = isGrainDirectionValid(pieceW, pieceL, 'horizontal', sheetW, sheetL);
    if (horizValid) return 'horizontal';
    const vertValid = isGrainDirectionValid(pieceW, pieceL, 'vertical', sheetW, sheetL);
    if (vertValid) return 'vertical';
    return 'none'; // fallback, will show warning
  };

  // Save new piece
  const saveNewPiece = () => {
    const widthVal = parseFloat(pieceForm.width) || 0;
    const lengthVal = parseFloat(pieceForm.length) || 0;
    const widthInches = widthVal;
    const lengthInches = lengthVal;
    // Thickness comes from the selected sheet in the form
    const thicknessInches = getThicknessForSheet(pieceForm.stockSheetTypeId);

    if (widthInches <= 0 || lengthInches <= 0) {
      addError('Width and length must be greater than 0');
      return;
    }

    // Save last used parameters (width and length only)
    try {
      localStorage.setItem(LAST_PIECE_PARAMS_KEY, JSON.stringify({
        width: widthVal,
        length: lengthVal,
      }));
    } catch (e) {
      console.error('Failed to save last piece params:', e);
    }

    addPiece({
      id: uid(),
      name: pieceForm.name,
      width: widthInches,
      length: lengthInches,
      thickness: thicknessInches,
      quantity: parseInt(pieceForm.quantity) || 1,
      material: 'Plywood',
      grainDirection: pieceForm.grainDirection,
      stockSheetTypeId: pieceForm.stockSheetTypeId,
    });
    setShowAddPieceModal(false);
  };

  // Open modal to add new stock type
  const openAddStockType = () => {
    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    setStockTypeForm({
      name: '',
      thickness: defs.thickness.toString(),
      width: defs.sheetW.toString(),
      length: defs.sheetL.toString()
    });
    setEditingStockType(null);
    setShowStockTypeModal(true);
  };

  // Open modal to edit existing stock type
  const openEditStockType = (stockType) => {
    setStockTypeForm({
      name: stockType.name,
      thickness: stockType.thickness.toString(),
      width: stockType.width.toString(),
      length: stockType.length.toString()
    });
    setEditingStockType(stockType);
    setShowStockTypeModal(true);
  };

  // Save stock type (add or update)
  const saveStockType = () => {
    const thickness = parseFloat(stockTypeForm.thickness) || 0;
    const width = parseFloat(stockTypeForm.width);
    const length = parseFloat(stockTypeForm.length);

    if (width <= 0 || length <= 0) {
      addError('Width and length must be greater than 0');
      return;
    }

    const name = stockTypeForm.name || `Stock ${stockSheetTypes.length + 1}`;

    if (editingStockType) {
      updateStockSheetType(editingStockType.id, {
        name,
        thickness: thickness,
        width: width,
        length: length
      });
    } else {
      const newId = addStockSheetType({
        name,
        thickness: thickness,
        width: width,
        length: length
      });
      // Auto-select the new sheet type
      setSelectedStockTypeId(newId);
    }
    setShowStockTypeModal(false);
  };

  // Inline editing state for stock type names
  const [editingStockTypeName, setEditingStockTypeName] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Handle double-click to edit stock type name
  const startEditingName = (stockType) => {
    setEditingStockTypeName(stockType.id);
    setEditingNameValue(stockType.name);
  };

  // Save edited name
  const saveEditedName = (stockTypeId) => {
    if (editingNameValue.trim()) {
      updateStockSheetType(stockTypeId, { name: editingNameValue.trim() });
    }
    setEditingStockTypeName(null);
  };

  // Drag and drop for pieces
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [dragOverStockType, setDragOverStockType] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const handlePieceDragStart = (e, piece) => {
    setDraggedPiece(piece);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', piece.id);
  };

  const handlePieceDragEnd = () => {
    setDraggedPiece(null);
    setDragOverStockType(null);
  };

  const handleStockTypeDragOver = (e, stockTypeId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStockType(stockTypeId);
  };

  const handleStockTypeDragLeave = () => {
    setDragOverStockType(null);
  };

  const handleStockTypeDrop = (e, targetStockTypeId) => {
    e.preventDefault();
    setDragOverStockType(null);

    if (draggedPiece) {
      // Find target sheet to get its thickness
      let targetThickness = getUnitDefaults(currentUnits.unit, currentUnits.system).thickness; // default
      if (targetStockTypeId !== 'unassigned') {
        const targetSheet = stockSheetTypes.find(t => t.id === targetStockTypeId);
        if (targetSheet) {
          targetThickness = targetSheet.thickness;
        }
      }

      // Update the piece's stock type assignment and thickness
      const newStockTypeId = targetStockTypeId === 'unassigned' ? null : targetStockTypeId;
      updatePiece(draggedPiece.id, 'stockSheetTypeId', newStockTypeId);
      updatePiece(draggedPiece.id, 'thickness', targetThickness);
      setDraggedPiece(null);
    }
  };

  // Group pieces by stock type for display
  const groupPiecesByStockType = () => {
    const groups = {};
    const tolerance = getThicknessTolerance(currentUnits.unit, currentUnits.system);

    // Initialize groups for all stock types
    stockSheetTypes.forEach(type => {
      groups[type.id] = { stockType: type, pieces: [] };
    });

    // Add unassigned group
    groups['unassigned'] = {
      stockType: {
        id: 'unassigned',
        name: 'Unassigned (Default)',
        width: sheetWidth,
        length: sheetLength,
        thickness: getUnitDefaults(currentUnits.unit, currentUnits.system).thickness
      },
      pieces: []
    };

    pieces.forEach(piece => {
      let stockType = null;

      // Check explicit assignment
      if (piece.stockSheetTypeId) {
        stockType = stockSheetTypes.find(t => t.id === piece.stockSheetTypeId);
      }

      // Auto-match by thickness if no explicit assignment
      if (!stockType) {
        stockType = stockSheetTypes.find(t =>
          Math.abs(t.thickness - (piece.thickness || 0)) < tolerance
        );
      }

      const key = stockType ? stockType.id : 'unassigned';
      if (groups[key]) {
        groups[key].pieces.push(piece);
      } else {
        groups['unassigned'].pieces.push(piece);
      }
    });

    // Return as array, filter out empty stock type groups (but keep unassigned if it has pieces)
    return Object.values(groups).filter(g =>
      g.pieces.length > 0 || (g.stockType.id !== 'unassigned')
    );
  };

  const stockTypeGroups = groupPiecesByStockType();

  // Count physical sheets needed per stock type from optimization results
  const getSheetsNeededForStockType = (stockTypeId) => {
    if (layouts.length === 0) return 0;
    // For unassigned, count layouts where stockTypeId is undefined or matches 'unassigned'
    if (stockTypeId === 'unassigned') {
      return layouts.filter(l => !l.stockTypeId || l.stockTypeId === 'unassigned' || l.stockTypeId === 'default').length;
    }
    return layouts.filter(l => l.stockTypeId === stockTypeId).length;
  };

  // File extensions for auto-detection
  const csvExtensions = ['.csv'];
  const model3DExtensions = ['.obj', '.gltf', '.glb'];

  // Format thickness for display
  const formatThickness = (thicknessVal) => {
    if (!thicknessVal || thicknessVal === 0) {
      return 'No thickness';
    }
    return formatDimension(thicknessVal, currentUnits.unit, currentUnits.system, { snap: true });
  };

  // Format sheet dimensions for display (with snap for clean values)
  const formatSheetSize = (width, length) => {
    const wStr = formatDimension(width, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true });
    const lStr = formatDimension(length, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true });
    return `${wStr}×${lStr} ${getUnitLabel()}`;
  };

  // Unified file processor with auto-detection
  const processDroppedFile = (file) => {
    if (!file) return;

    const fileName = file.name.toLowerCase();

    // Check if CSV
    if (csvExtensions.some(ext => fileName.endsWith(ext))) {
      processCSVFile(file);
      return;
    }

    // Check if 3D model
    if (model3DExtensions.some(ext => fileName.endsWith(ext))) {
      setPending3DFile(file);
      setShow3DImporter(true);
      return;
    }

    // Unknown file type
    addError(`Unsupported file type. Please use CSV (.csv) or 3D model (.obj, .gltf, .glb) files.`);
  };

  // Global drag-drop handlers
  // Check if a drag event contains files (external) vs internal piece drag
  const isFileDrag = (e) => {
    return e.dataTransfer.types.includes('Files');
  };

  const handleGlobalDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleGlobalDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show file drop UI for actual file drags, not internal piece drags
    if (isFileDrag(e)) {
      e.dataTransfer.dropEffect = 'copy';
      if (!isDragging) setIsDragging(true);
    }
  };

  const handleGlobalDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if leaving the window
    if (e.relatedTarget === null || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleGlobalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Only process file drops, not internal piece drops
    if (!isFileDrag(e)) return;

    const file = e.dataTransfer.files?.[0];
    processDroppedFile(file);
  };

  // Process a CSV file (used by both click upload and drag-drop)
  const processCSVFile = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      addError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          addError('CSV file must have at least a header row and one data row');
          return;
        }

        // Check if first row is just a title (single cell or significantly fewer columns than row 2)
        const firstRowCells = lines[0].split(',').map(h => h.trim());
        const secondRowCells = lines[1].split(',').map(h => h.trim());

        // First row is a title if it has only 1 non-empty cell, or significantly fewer columns
        const firstRowNonEmpty = firstRowCells.filter(c => c.length > 0).length;
        const isFirstRowTitle = firstRowNonEmpty <= 1 && secondRowCells.length > 1;

        let headers, dataStartIndex;
        if (isFirstRowTitle) {
          // First row is just a title, use second row as headers
          headers = secondRowCells;
          dataStartIndex = 2;
        } else {
          // Normal case: first row is headers
          headers = firstRowCells;
          dataStartIndex = 1;
        }

        if (lines.length <= dataStartIndex) {
          addError('CSV file must have at least a header row and one data row');
          return;
        }

        const rows = [];
        for (let i = dataStartIndex; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          rows.push(values);
        }

        setCsvData({ headers, rows });
        setShowColumnMapper(true);
      } catch (error) {
        addError(`Error processing CSV: ${error.message}`);
      }
    };
    reader.onerror = () => addError('Error reading CSV file');
    reader.readAsText(file);
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files?.[0];
    processCSVFile(file);
    // Reset input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  // CSV-specific drag handlers (for the CSV tab zone)
  const handleCSVDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only respond to file drags, not internal piece drags
    if (isFileDrag(e)) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragging(true);
    }
  };

  const handleCSVDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleCSVDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Only process file drops
    if (!isFileDrag(e)) return;

    const file = e.dataTransfer.files?.[0];
    processCSVFile(file);
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Handle opening a .cutcalc project file
  const handleOpenProject = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const project = await readCutcalcFile(file);
      const validation = validateProject(project);

      if (!validation.valid) {
        addError(`Invalid project file: ${validation.errors.join(', ')}`);
        return;
      }

      // Load project settings
      if (project.settings.currentUnits) {
        setCurrentUnits(project.settings.currentUnits);
      }
      if (typeof project.settings.sheetWidth === 'number') {
        setSheetWidth(project.settings.sheetWidth);
      }
      if (typeof project.settings.sheetLength === 'number') {
        setSheetLength(project.settings.sheetLength);
      }
      if (typeof project.settings.cutWidth === 'number') {
        setCutWidth(project.settings.cutWidth);
      }
      // Always set trim values (use defaults if not present in file)
      setTrimFactoryEdges(project.settings.trimFactoryEdges === true);
      setTrimAmount(typeof project.settings.trimAmount === 'number' ? project.settings.trimAmount : 0);

      // Load stock sheet types if present
      if (project.stockSheetTypes && project.stockSheetTypes.length > 0) {
        setStockSheetTypes(project.stockSheetTypes);
      }

      // Load pieces if present
      if (project.pieces && project.pieces.length > 0) {
        setPieces(project.pieces);
      }

      // Load layouts
      setLayouts(project.layouts);

      // Load stats if present
      if (project.stats) {
        setStats(project.stats);
      }

      // Load model snapshot if present
      if (project.modelSnapshot) {
        setModelSnapshot(project.modelSnapshot);
      }

      // Open shop sheet view (use setTimeout to ensure state updates are flushed)
      setTimeout(() => setExecutionMode('shopsheet'), 0);

    } catch (err) {
      addError(err.message || 'Failed to open project file');
    }

    // Reset input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleClickOpenProject = () => {
    projectFileInputRef.current?.click();
  };

  const processCSVWithMapping = async (columnMapping) => {
    // Check server-side rate limit for CSV import
    const rateCheck = await checkImportAllowed('csv');
    if (!rateCheck.allowed) {
      setShowColumnMapper(false);
      return; // promptUpgrade is called inside checkImportAllowed
    }

    const { rows } = csvData;
    const newPieces = [];

    const widthIdx = columnMapping.width !== '' ? parseInt(columnMapping.width) : -1;
    const lengthIdx = columnMapping.length !== '' ? parseInt(columnMapping.length) : (columnMapping.height !== '' ? parseInt(columnMapping.height) : -1);
    const depthIdx = columnMapping.depth !== '' ? parseInt(columnMapping.depth) : -1;
    const quantityIdx = columnMapping.quantity !== '' ? parseInt(columnMapping.quantity) : -1;
    const nameIdx = columnMapping.name !== '' ? parseInt(columnMapping.name) : -1;
    const materialIdx = columnMapping.material !== '' ? parseInt(columnMapping.material) : -1;
    const grainIdx = columnMapping.grainDirection !== '' ? parseInt(columnMapping.grainDirection) : -1;
    const stockTypeIdx = columnMapping.stockType !== '' && columnMapping.stockType !== undefined ? parseInt(columnMapping.stockType) : -1;
    const sheetDimsIdx = columnMapping.sheetDimensions !== '' && columnMapping.sheetDimensions !== undefined ? parseInt(columnMapping.sheetDimensions) : -1;
    const detectedUnit = columnMapping.detectedUnit;

    // No conversion needed — values stored as-is in the user's current unit
    const sheetDimsFactor = 1;

    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    const defaultThickness = defs.thickness;

    if (widthIdx === -1 || lengthIdx === -1) {
      addError('Width and Length are required fields');
      setShowColumnMapper(false);
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      const values = rows[i];
      if (values.length < Math.max(widthIdx, lengthIdx) + 1) continue;

      let width = parseFloat(values[widthIdx] || '0');
      let length = parseFloat(values[lengthIdx] || '0');
      let thickness = depthIdx !== -1 ? parseFloat(values[depthIdx] || '0') : 0;

      // Skip rows where width/length are not valid numbers (e.g., header rows with text)
      if (isNaN(width) || isNaN(length)) continue;

      // Values stored as-is — no conversion
      thickness = thickness > 0 ? thickness : defaultThickness;

      if (width <= 0 || length <= 0) continue;

      // Parse sheet dimensions from CSV (e.g., "2440x1220x18")
      const rawSheetDims = sheetDimsIdx !== -1 ? (values[sheetDimsIdx] || '').replace(/"/g, '').trim() : '';

      newPieces.push({
        id: uid(),
        name: nameIdx !== -1 ? values[nameIdx]?.replace(/"/g, '') : `Part ${i + 1}`,
        width: parseFloat(width.toFixed(4)),
        length: parseFloat(length.toFixed(4)),
        thickness: parseFloat(thickness.toFixed(4)),
        quantity: quantityIdx !== -1 ? parseInt(values[quantityIdx]) || 1 : 1,
        material: materialIdx !== -1 ? values[materialIdx] : 'Plywood',
        grainDirection: grainIdx !== -1 ? (() => {
          const g = (values[grainIdx] || '').toLowerCase().trim();
          if (g === 'along length' || g === 'horizontal' || g === 'length' || g === 'lengthwise' || g === 'long') return 'horizontal';
          if (g === 'along width' || g === 'vertical' || g === 'width' || g === 'widthwise' || g === 'short') return 'vertical';
          return 'none';
        })() : 'none',
        stockSheetTypeId: null,
        _csvSheetName: stockTypeIdx !== -1 ? (values[stockTypeIdx] || '').replace(/"/g, '').trim() : '',
        _csvSheetDims: rawSheetDims
      });
    }

    if (newPieces.length > 0) {
      let piecesToAdd = newPieces;

      // Auto-create stock sheet types from imported pieces grouped by thickness + sheet type name
      const THICKNESS_TOL = getThicknessTolerance(currentUnits.unit, currentUnits.system);
      const groupMap = new Map();

      piecesToAdd.forEach((piece, idx) => {
        const csvSheetName = piece._csvSheetName || '';
        const thickness = piece.thickness || defaultThickness;

        let matched = false;
        for (const [, group] of groupMap) {
          if (Math.abs(group.thickness - thickness) < THICKNESS_TOL && group.name.toLowerCase() === csvSheetName.toLowerCase()) {
            group.pieceIndices.push(idx);
            if (!group.sheetDims && piece._csvSheetDims) group.sheetDims = piece._csvSheetDims;
            matched = true;
            break;
          }
        }
        if (!matched) {
          const key = `${thickness.toFixed(4)}|${csvSheetName}`;
          groupMap.set(key, { thickness, name: csvSheetName, pieceIndices: [idx], sheetDims: piece._csvSheetDims || '' });
        }
      });

      let sheetCounter = stockSheetTypes.length + 1;
      for (const [, group] of groupMap) {
        let existingType = stockSheetTypes.find(t =>
          Math.abs(t.thickness - group.thickness) < THICKNESS_TOL &&
          (group.name ? t.name.toLowerCase() === group.name.toLowerCase() : true)
        );

        if (!existingType) {
          const name = group.name && group.name.toLowerCase() !== 'unassigned'
            ? group.name
            : `Stock ${sheetCounter++}`;
          // Parse sheet dimensions from CSV (e.g., "2440x1220x18")
          let sheetW = defs.sheetW, sheetL = defs.sheetL;
          if (group.sheetDims) {
            const parts = group.sheetDims.split(/x/i).map(s => parseFloat(s.trim()));
            if (parts.length >= 2 && parts[0] > 0 && parts[1] > 0) {
              sheetW = parseFloat((parts[0] * sheetDimsFactor).toFixed(4));
              sheetL = parseFloat((parts[1] * sheetDimsFactor).toFixed(4));
            }
          }
          const newId = addStockSheetType({
            name,
            thickness: group.thickness,
            width: sheetW,
            length: sheetL
          });
          existingType = { id: newId };
        }

        group.pieceIndices.forEach(idx => {
          piecesToAdd[idx] = { ...piecesToAdd[idx], stockSheetTypeId: existingType.id };
        });
      }

      piecesToAdd.forEach(p => { delete p._csvSheetName; delete p._csvSheetDims; });

      addPieces(piecesToAdd);

      // Switch display units to match detected CSV unit
      if (detectedUnit) {
        const unitMapping = {
          'mm': { system: 'metric', unit: 'mm' },
          'cm': { system: 'metric', unit: 'cm' },
          'm': { system: 'metric', unit: 'm' },
          'in': { system: 'imperial', unit: 'inches' },
          'ft': { system: 'imperial', unit: 'feet' }
        };
        const unitConfig = unitMapping[detectedUnit];
        if (unitConfig) {
          handleUnitChange(unitConfig);
        }
      }

      if (piecesToAdd.length === newPieces.length) {
        addError(`Successfully imported ${newPieces.length} pieces`);
      }
      setActiveInputTab('manual'); // Switch to manual tab to show imported pieces
    } else {
      addError('No valid pieces found in CSV');
    }

    setShowColumnMapper(false);
  };

  const exportCSV = () => {
    const unitLabel = getCSVUnitLabel(currentUnits.unit, currentUnits.system);
    const rows = [
      [`Part Name`, `Width (${unitLabel})`, `Length (${unitLabel})`, `Thickness (${unitLabel})`, `Quantity`, `Grain`, `Sheet Type`, `Sheet Dimensions (${unitLabel})`].join(',')
    ];

    const tolerance = getThicknessTolerance(currentUnits.unit, currentUnits.system);
    pieces.forEach(piece => {
      let sheet = piece.stockSheetTypeId ? stockSheetTypes.find(t => t.id === piece.stockSheetTypeId) : null;
      if (!sheet) {
        sheet = stockSheetTypes.find(t => Math.abs(t.thickness - (piece.thickness || 0)) < tolerance);
      }
      const conv = (val) => roundForCSV(val, currentUnits.unit, currentUnits.system);
      const esc = (str) => `"${(str || '').replace(/"/g, '""')}"`;
      const sheetDims = sheet ? `${conv(sheet.width)}x${conv(sheet.length)}x${conv(sheet.thickness)}` : '';

      rows.push([
        esc(piece.name),
        conv(piece.width),
        conv(piece.length),
        conv(piece.thickness),
        piece.quantity || 1,
        piece.grainDirection === 'horizontal' ? 'Along Length' : piece.grainDirection === 'vertical' ? 'Along Width' : 'No Preference',
        sheet ? esc(sheet.name) : 'Unassigned',
        sheetDims
      ].join(','));
    });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cut-list.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getUnitLabel = () => {
    if (currentUnits.system === UNIT_SYSTEMS.IMPERIAL) {
      return currentUnits.unit === 'inches' ? 'in' : 'ft';
    }
    return currentUnits.unit;
  };

  // Handle 3D model import
  const handle3DImport = (importedPieces, modelSnapshot) => {
    if (importedPieces && importedPieces.length > 0) {
      let piecesToAdd = importedPieces;
      addError(`Successfully imported ${piecesToAdd.length} pieces`);

      // Group pieces by thickness and auto-create stock sheet types
      const THICKNESS_TOL = getThicknessTolerance(currentUnits.unit, currentUnits.system);
      const thicknessGroups = new Map();

      piecesToAdd.forEach(piece => {
        const t = piece.thickness || 0;
        // Find existing group within tolerance
        let matchedKey = null;
        for (const [key] of thicknessGroups) {
          if (Math.abs(key - t) < THICKNESS_TOL) {
            matchedKey = key;
            break;
          }
        }
        if (matchedKey !== null) {
          thicknessGroups.get(matchedKey).push(piece);
        } else {
          thicknessGroups.set(t, [piece]);
        }
      });

      // Create stock sheet types for each thickness group and assign pieces
      const updatedPieces = [];

      // Build new sheet types first, then batch-add them
      const newSheetTypes = [];
      const thicknessToSheetId = new Map();

      // First, map existing stock sheet types by thickness
      stockSheetTypes.forEach(st => {
        thicknessToSheetId.set(st.thickness, st.id);
      });

      thicknessGroups.forEach((groupPieces, thickness) => {
        // Check if a sheet type already exists for this thickness (existing or just created)
        let sheetTypeId = null;
        for (const [existingThickness, existingId] of thicknessToSheetId) {
          if (Math.abs(existingThickness - thickness) < THICKNESS_TOL) {
            sheetTypeId = existingId;
            break;
          }
        }

        if (!sheetTypeId) {
          // Create a new sheet type with a unique ID
          sheetTypeId = uid();
          const stockIndex = stockSheetTypes.length + newSheetTypes.length + 1;
          newSheetTypes.push({
            id: sheetTypeId,
            name: `Stock ${stockIndex}`,
            width: sheetWidth,
            length: sheetLength,
            thickness: thickness
          });
          // Track so next group can find it
          thicknessToSheetId.set(thickness, sheetTypeId);
        }

        // Assign each piece to its stock sheet type
        groupPieces.forEach(piece => {
          updatedPieces.push({
            ...piece,
            stockSheetTypeId: sheetTypeId
          });
        });
      });

      // Batch-add all new sheet types at once
      if (newSheetTypes.length > 0) {
        setStockSheetTypes(prev => [...prev, ...newSheetTypes]);
      }

      addPieces(updatedPieces);
      setActiveInputTab('manual');

      if (thicknessGroups.size > 0) {
        addError(`Created ${thicknessGroups.size} sheet type${thicknessGroups.size !== 1 ? 's' : ''}`);
      }

      // Store 3D model snapshot for print view, recolored to match cut layout
      if (modelSnapshot && modelSnapshot.meshToPieceDims) {
        // Build mesh UUID → layout color map by matching dimensions
        // Same tolerance logic as mergeSimilarPieces
        const meshColorMap = new Map();
        const dimTolerance = 0.5;
        const thicknessTolerance = getThicknessTolerance(currentUnits.unit, currentUnits.system);

        // Pieces are now in app units — no conversion needed
        const factor = 1;

        // Build list of final pieces with colors
        const pieceColorEntries = updatedPieces.map((piece) => ({
          width: piece.width,
          length: piece.length,
          thickness: piece.thickness || 0,
          grainDirection: piece.grainDirection,
          color: getPartColor(piece.id).hex,
        }));

        // Match each mesh to a final piece by dimensions (same as merge logic)
        Object.entries(modelSnapshot.meshToPieceDims).forEach(([meshUuid, dims]) => {
          // Convert mesh dims to inches to match updatedPieces
          const mw = dims.width * factor;
          const mh = dims.height * factor;
          const mt = dims.thickness * factor;

          const match = pieceColorEntries.find(p => {
            const sameThickness = Math.abs(p.thickness - mt) < thicknessTolerance;
            const sameDims = (
              (Math.abs(p.width - mw) < dimTolerance && Math.abs(p.length - mh) < dimTolerance) ||
              (Math.abs(p.width - mh) < dimTolerance && Math.abs(p.length - mw) < dimTolerance)
            );
            return sameThickness && sameDims;
          });

          if (match) {
            meshColorMap.set(meshUuid, match.color);
          }
        });

        // Recolor the faces version for color mode, plain edges for B&W
        const coloredSvg = recolorSVG(modelSnapshot.dataWithFaces || modelSnapshot.data, meshColorMap);
        setModelSnapshot({
          ...modelSnapshot,
          dataColor: coloredSvg,
          dataBW: modelSnapshot.data,
        });
      } else if (modelSnapshot) {
        setModelSnapshot(modelSnapshot);
      }
    }
  };

  return (
    <div
      className="h-screen flex flex-col bg-gray-100"
      onDragEnter={handleGlobalDragEnter}
      onDragOver={handleGlobalDragOver}
      onDragLeave={handleGlobalDragLeave}
      onDrop={handleGlobalDrop}
    >
      {/* Grain Warning Toast - only for "too large" errors, not "locked" messages (shown inline) */}
      {grainWarning && !grainWarning.includes('limited by') && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{grainWarning}</span>
        </div>
      )}
      {/* Header Bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={iconImg} alt="CutCalc" className="w-14 h-12" />
          <h1 className="text-xl font-bold text-gray-900">CutCalc</h1>
        </div>
        <div className="flex items-center gap-2">
          {onShowAbout && (
            <button
              onClick={onShowAbout}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="About CutCalc"
            >
              <Info className="w-4 h-4" />
            </button>
          )}
          {onSwitchToMobile && (
            <button
              onClick={onSwitchToMobile}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Switch to Mobile View"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          )}
          <UnitSelector onUnitChange={handleUnitChange} />
          {/* Free/Pro Button */}
          <button
            onClick={isPro ? undefined : () => promptUpgrade('Upgrade to Pro for advanced optimization algorithms and more features.')}
            className={`flex items-center justify-center px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
              isPro
                ? 'bg-transparent border-sage-500 text-sage-600 cursor-default'
                : 'bg-[#eb5324] border-[#eb5324] text-white hover:bg-[#d44a1f] hover:border-[#d44a1f] cursor-pointer'
            }`}
            title={isPro ? 'Pro license active' : 'Click to upgrade to Pro'}
          >
            {isPro ? 'Pro' : 'Free Version'}
          </button>
        </div>
      </header>

      {/* Error Toast - cascading right-to-left from header */}
      {errors.length > 0 && (
        <div className="fixed z-50 flex flex-row-reverse items-center gap-2" style={{ top: '18px', right: '300px', maxWidth: 'calc(100vw - 360px)' }}>
          {errors.map((error) => (
            <div
              key={error.id}
              className="px-3 py-1.5 rounded-lg shadow-lg text-white whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: error.message.includes('Success') ? '#489b6e' :
                  error.message.includes('Created') ? '#489b6e' :
                  error.message.includes('Warning') ? '#fcb315' : '#eb5324'
              }}
            >
              <span className="text-sm">{error.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Three-Pane Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Pane - Input */}
        <div className="w-1/3 min-w-[320px] max-w-[480px] bg-white border-r border-gray-200 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveInputTab('manual')}
              className={`flex-1 px-3 py-3 text-sm font-medium ${
                activeInputTab === 'manual'
                  ? 'text-accent-500 border-b-2 border-accent-400 bg-accent-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Manual
            </button>
            <button
              onClick={() => setActiveInputTab('import')}
              className={`flex-1 px-3 py-3 text-sm font-medium ${
                activeInputTab === 'import'
                  ? 'text-accent-500 border-b-2 border-accent-400 bg-accent-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              CSV
            </button>
            <button
              onClick={() => setActiveInputTab('3d')}
              className={`flex-1 px-3 py-3 text-sm font-medium flex items-center justify-center gap-1 ${
                activeInputTab === '3d'
                  ? 'text-accent-500 border-b-2 border-accent-400 bg-accent-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Box className="w-4 h-4" />
              3D
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeInputTab === 'manual' && (
              <div className="space-y-3">
                {/* Add Piece & Add Sheet Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={openAddPieceModal}
                    className="flex-[2] flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors font-semibold shadow-sm"
                  >
                    <Plus className="w-5 h-5" />
                    Add Piece
                  </button>
                  <button
                    onClick={openAddStockType}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-sage-300 text-sage-700 rounded-lg hover:border-sage-400 hover:bg-sage-50 transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Sheet
                  </button>
                  <button
                    onClick={exportCSV}
                    disabled={pieces.length === 0}
                    className="flex items-center justify-center px-3 py-3 border-2 border-sage-300 text-sage-700 rounded-lg hover:border-sage-400 hover:bg-sage-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Download CSV"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>

                {/* Pieces List - Grouped by Stock Type */}
                {stockTypeGroups.map((group) => (
                  <div
                    key={`stocktype-${group.stockType.id}`}
                    className={`border-2 rounded-lg overflow-hidden transition-all ${
                      dragOverStockType === group.stockType.id
                        ? 'border-accent-500 ring-2 ring-accent-200'
                        : selectedStockTypeId === group.stockType.id
                        ? 'border-accent-400'
                        : group.stockType.id === 'unassigned'
                        ? 'border-gray-300'
                        : 'border-sage-300'
                    }`}
                    onDragOver={(e) => handleStockTypeDragOver(e, group.stockType.id)}
                    onDragLeave={handleStockTypeDragLeave}
                    onDrop={(e) => handleStockTypeDrop(e, group.stockType.id)}
                  >
                    {/* Group Header */}
                    <div
                      className={`px-3 py-2 border-b cursor-pointer transition-colors ${
                        dragOverStockType === group.stockType.id
                          ? 'bg-accent-200 border-accent-300'
                          : selectedStockTypeId === group.stockType.id
                          ? 'bg-accent-100 border-accent-200'
                          : group.stockType.id === 'unassigned'
                          ? 'bg-gray-100 border-gray-200'
                          : 'bg-sage-100 border-sage-200 hover:bg-sage-150'
                      }`}
                      onClick={() => {
                        if (group.stockType.id !== 'unassigned' && editingStockTypeName !== group.stockType.id) {
                          setSelectedStockTypeId(
                            selectedStockTypeId === group.stockType.id ? null : group.stockType.id
                          );
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {editingStockTypeName === group.stockType.id ? (
                            <input
                              type="text"
                              value={editingNameValue}
                              onChange={(e) => setEditingNameValue(e.target.value)}
                              onBlur={() => saveEditedName(group.stockType.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditedName(group.stockType.id);
                                if (e.key === 'Escape') setEditingStockTypeName(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                              className="text-sm font-semibold text-gray-700 bg-white border border-accent-400 rounded px-2 py-0.5 focus:outline-none"
                            />
                          ) : (
                            <span
                              className="text-sm font-semibold text-gray-700 cursor-text"
                              onDoubleClick={(e) => {
                                if (group.stockType.id !== 'unassigned') {
                                  e.stopPropagation();
                                  startEditingName(group.stockType);
                                }
                              }}
                              title={group.stockType.id !== 'unassigned' ? 'Double-click to rename' : ''}
                            >
                              {group.stockType.name}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {formatSheetSize(group.stockType.width, group.stockType.length)} • {formatThickness(group.stockType.thickness)} • {group.pieces.length} piece{group.pieces.length !== 1 ? 's' : ''}
                            {getSheetsNeededForStockType(group.stockType.id) > 0 && (
                              <span className="ml-1 px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded font-medium">
                                → {getSheetsNeededForStockType(group.stockType.id)} sheet{getSheetsNeededForStockType(group.stockType.id) !== 1 ? 's' : ''} needed
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {group.stockType.id !== 'unassigned' && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditStockType(group.stockType);
                                }}
                                className="p-1 text-gray-400 hover:text-accent-500 transition-colors"
                                title="Edit sheet type"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm({ id: group.stockType.id, name: group.stockType.name });
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete sheet type"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Group Pieces */}
                    <div className="p-2 space-y-2 bg-white min-h-[40px]">
                      {group.pieces.map(piece => (
                        <div
                          key={piece.id}
                          draggable
                          onDragStart={(e) => handlePieceDragStart(e, piece)}
                          onDragEnd={handlePieceDragEnd}
                          className={`border rounded-lg p-3 transition-colors cursor-grab active:cursor-grabbing ${
                            draggedPiece?.id === piece.id
                              ? 'opacity-50 border-accent-400'
                              : highlightedPieceId === piece.id
                              ? 'border-accent-400 bg-accent-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onMouseEnter={() => setHighlightedPieceId(piece.id)}
                          onMouseLeave={() => setHighlightedPieceId(null)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <input
                              type="text"
                              value={piece.name}
                              onChange={(e) => updatePiece(piece.id, 'name', e.target.value)}
                              className="font-medium text-sm bg-transparent border-b border-transparent hover:border-gray-300 focus:border-accent-400 focus:outline-none px-1 py-0.5 -ml-1"
                            />
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  const cycleOrder = ['none', 'horizontal', 'vertical'];
                                  const currentIdx = cycleOrder.indexOf(piece.grainDirection || 'none');
                                  const sheetW = group.stockType.width;
                                  const sheetL = group.stockType.length;
                                  for (let i = 1; i <= 3; i++) {
                                    const nextGrain = cycleOrder[(currentIdx + i) % 3];
                                    if (isGrainDirectionValid(piece.width, piece.length, nextGrain, sheetW, sheetL)) {
                                      updatePiece(piece.id, 'grainDirection', nextGrain);
                                      setGrainWarning(null);
                                      return;
                                    }
                                  }
                                  setGrainWarning('Grain direction limited by sheet dimensions');
                                  setTimeout(() => setGrainWarning(null), 3000);
                                }}
                                title={piece.grainDirection === 'horizontal' ? 'Grain: Along Length' : piece.grainDirection === 'vertical' ? 'Grain: Along Width' : 'Grain: No Preference'}
                                className={`p-1.5 rounded transition-colors hover:brightness-95 ${
                                  !isGrainDirectionValid(piece.width, piece.length, piece.grainDirection || 'none', group.stockType.width, group.stockType.length)
                                    ? 'text-red-600 bg-red-50'
                                    : 'text-gray-600'
                                }`}
                                style={isGrainDirectionValid(piece.width, piece.length, piece.grainDirection || 'none', group.stockType.width, group.stockType.length) ? { backgroundColor: '#E5E7EB' } : {}}
                              >
                                {piece.grainDirection === 'horizontal' ? <ArrowLeftRight className="w-4 h-4" /> : piece.grainDirection === 'vertical' ? <ArrowUpDown className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => removePiece(piece.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <label className="text-gray-500">W ({getUnitLabel()})</label>
                              <input
                                type="number"
                                value={formatDimension(piece.width, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                                title={formatPreciseDimension(piece.width, currentUnits.unit, currentUnits.system)}
                                onChange={(e) => {
                                  const val = parseValue(e.target.value, currentUnits.unit, currentUnits.system);
                                  updatePiece(piece.id, 'width', val);
                                }}
                                className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-accent-400 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-gray-500">L ({getUnitLabel()})</label>
                              <input
                                type="number"
                                value={formatDimension(piece.length, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                                title={formatPreciseDimension(piece.length, currentUnits.unit, currentUnits.system)}
                                onChange={(e) => {
                                  const val = parseValue(e.target.value, currentUnits.unit, currentUnits.system);
                                  updatePiece(piece.id, 'length', val);
                                }}
                                className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-accent-400 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-gray-500">Qty</label>
                              <input
                                type="number"
                                min="1"
                                value={piece.quantity}
                                onChange={(e) => updatePiece(piece.id, 'quantity', parseInt(e.target.value) || 1)}
                                onFocus={(e) => e.target.select()}
                                className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-accent-400 focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {group.pieces.length === 0 && group.stockType.id !== 'unassigned' && (
                        <div className={`text-center py-4 text-sm border-2 border-dashed rounded-lg ${
                          dragOverStockType === group.stockType.id
                            ? 'border-accent-400 bg-accent-50 text-accent-600'
                            : 'border-gray-200 text-gray-400'
                        }`}>
                          {dragOverStockType === group.stockType.id
                            ? 'Drop piece here'
                            : 'Drag pieces here or select and click "Add Piece"'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {pieces.length === 0 && stockSheetTypes.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <p>No pieces yet</p>
                    <p className="text-sm">Click "Add Sheet" to define your stock, then "Add Piece"</p>
                  </div>
                )}
              </div>
            )}

            {activeInputTab === 'import' && (
              <div className="space-y-4">
                {/* CSV Import Zone */}
                <div
                  onClick={handleClickUpload}
                  onDragOver={handleCSVDragOver}
                  onDragLeave={handleCSVDragLeave}
                  onDrop={handleCSVDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-accent-400 bg-accent-100'
                      : 'border-gray-300 hover:border-accent-400 hover:bg-accent-50'
                  }`}
                >
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-accent-500' : 'text-gray-400'}`} />
                  <p className={isDragging ? 'text-accent-600' : 'text-gray-600'}>
                    {isDragging ? 'Drop CSV file here' : 'Drop CSV file here or click to browse'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Supports standard CSV format</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  style={{ display: 'none' }}
                />

                {/* Export Button */}
                <button
                  onClick={exportCSV}
                  disabled={pieces.length === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Export Current List as CSV
                </button>
              </div>
            )}

            {activeInputTab === '3d' && (
              <div className="space-y-4">
                {/* 3D Import Zone */}
                <div
                  onClick={() => setShow3DImporter(true)}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-accent-400 hover:bg-accent-50 transition-colors"
                >
                  <Box className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600">Import from 3D Model</p>
                  <p className="text-xs text-gray-400 mt-1">Supports OBJ, GLTF, GLB files</p>
                </div>

                <div className="bg-sage-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 text-sm mb-2">How it works</h4>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>1. Load your 3D model file</li>
                    <li>2. CutCalc detects flat pieces</li>
                    <li>3. Select which pieces to import</li>
                    <li>4. Dimensions are extracted automatically</li>
                  </ul>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Best for furniture designs exported from SketchUp, Fusion 360, or similar CAD software
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Center Pane - Cut Settings */}
        <div className="w-64 bg-sage-100 border-r border-gray-200 flex flex-col">
          {/* Cut Settings Header */}
          <div className="p-4 border-b border-sage-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Cut Settings</h2>
            <div>
              <label className="text-xs text-gray-500">Blade Kerf ({getUnitLabel()})</label>
              <input
                type="number"
                step="0.001"
                key={`kerf-${cutWidth}-${currentUnits.unit}`}
                defaultValue={(() => {
                  const val = cutWidth;
                  if (!val || val === 0) return '0.0';
                  const str = val.toFixed(3).replace(/\.?0+$/, '');
                  return str.includes('.') ? str : str + '.0';
                })()}
                title={formatPreciseDimension(cutWidth, currentUnits.unit, currentUnits.system)}
                onBlur={(e) => {
                  const val = parseValue(e.target.value || '0', currentUnits.unit, currentUnits.system);
                  setCutWidth(val || 0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none bg-white"
              />
              <p className="text-xs text-gray-400 mt-1">Standard: ~3mm / 1/8"</p>

              {/* Trim Factory Edges */}
              <label className="flex items-center gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={trimFactoryEdges}
                  onChange={(e) => setTrimFactoryEdges(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-accent-500 focus:ring-accent-400"
                />
                <span className="text-xs text-gray-500">Trim Factory Edges</span>
              </label>
              {trimFactoryEdges && (
                <div className="mt-2">
                  <label className="text-xs text-gray-500">Trim Amount ({getUnitLabel()})</label>
                  <input
                    type="number"
                    step="0.0625"
                    min="0"
                    key={`trim-${trimAmount}-${currentUnits.unit}`}
                    defaultValue={(() => {
                      const val = trimAmount;
                      if (!val || val === 0) return '0.0';
                      const str = val.toFixed(3).replace(/\.?0+$/, '');
                      return str.includes('.') ? str : str + '.0';
                    })()}
                    onBlur={(e) => {
                      const val = parseValue(e.target.value || '0', currentUnits.unit, currentUnits.system);
                      setTrimAmount(val || 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none bg-white"
                  />
                  <p className="text-xs text-gray-400 mt-1">Per edge of each sheet</p>
                </div>
              )}
            </div>
          </div>

          {/* Algorithm Selector - Dropdown */}
          <div className="p-4 border-b border-sage-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Algorithm</h2>
            <div className="relative">
              {/* Selected Algorithm Button */}
              <button
                onClick={() => setAlgorithmDropdownOpen(!algorithmDropdownOpen)}
                className="w-full text-left px-3 py-2 rounded-lg border border-accent-400 bg-accent-50 text-accent-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{ALGORITHM_INFO[algorithm]?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-accent-500">
                      {ALGORITHM_INFO[algorithm]?.efficiency}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${algorithmDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{ALGORITHM_INFO[algorithm]?.description}</p>
              </button>

              {/* Dropdown Options */}
              {algorithmDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-[70vh] overflow-y-auto">
                  {Object.entries(ALGORITHM_INFO).map(([key, info]) => {
                    const isAvailable = canUseAlgorithm(key);
                    const isSelected = algorithm === key;

                    return (
                      <button
                        key={key}
                        onClick={() => {
                          if (isAvailable) {
                            setAlgorithm(key);
                            setAlgorithmDropdownOpen(false);
                          } else {
                            promptUpgrade(`Upgrade to Pro to use the ${info.name} algorithm for better material efficiency.`);
                          }
                        }}
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 transition-colors ${
                          isSelected
                            ? 'bg-accent-50 text-accent-700'
                            : isAvailable
                            ? 'bg-white hover:bg-gray-50 text-gray-700'
                            : 'bg-gray-50 text-gray-500 opacity-75'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${!isAvailable && 'text-gray-500'}`}>{info.name}</span>
                            {!isAvailable && <Lock className="w-3 h-3 text-gray-400" />}
                            {isSelected && <Check className="w-3 h-3 text-accent-500" />}
                          </div>
                          <div className="flex items-center gap-2">
                            {!isAvailable && (
                              <span className="text-xs px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded font-medium">
                                PRO
                              </span>
                            )}
                            <span className={`text-xs ${isSelected ? 'text-accent-500' : 'text-gray-400'}`}>
                              {info.efficiency}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{info.description}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Optimize Button */}
          <div className="p-4 border-b border-sage-200">
            <button
              onClick={optimize}
              disabled={pieces.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-400 text-white rounded-lg hover:bg-accent-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md transition-colors"
            >
              <Play className="w-5 h-5" />
              Optimize
            </button>
          </div>

          {/* Spacer + Shop Sheet + Open Project */}
          <div className="flex-1" />
          <div className="px-4 pb-4 space-y-2">
            {layouts.length > 0 && (
              <button
                onClick={() => {
                  if (isPro) {
                    setExecutionMode('shopsheet');
                  } else {
                    setShowPreviewModal(true);
                  }
                }}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition-colors text-sm font-medium ${
                  isPro
                    ? 'bg-sage-50 text-sage-700 border-sage-200 hover:bg-sage-100'
                    : 'bg-accent-50 text-accent-600 border-accent-200 hover:bg-accent-100'
                }`}
              >
                <FileImage className="w-4 h-4" />
                {isPro ? 'Shop Sheet' : 'Preview Shop Sheet'}
              </button>
            )}
            <button
              onClick={() => {
                if (isPro) {
                  handleClickOpenProject();
                } else {
                  promptUpgrade('Upgrade to Pro to open and import project files.');
                }
              }}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition-colors text-sm font-medium ${
                isPro
                  ? 'text-gray-600 border-gray-200 hover:bg-gray-50'
                  : 'bg-gray-50 text-gray-500 border-gray-200 opacity-75'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              Open Project
              {!isPro && <Lock className="w-3 h-3 text-gray-400" />}
              {!isPro && (
                <span className="text-xs px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded font-medium">
                  PRO
                </span>
              )}
            </button>
            <input
              ref={projectFileInputRef}
              type="file"
              accept=".cutcalc,.json"
              onChange={handleOpenProject}
              className="hidden"
            />
          </div>

          {/* Reset Button */}
          {(pieces.length > 0 || layouts.length > 0) && (
            <div className="p-4 border-t border-sage-200">
              <button
                onClick={resetAll}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
            </div>
          )}
        </div>

        {/* Right Pane - Visualization */}
        <div className="flex-1 bg-sage-50 overflow-y-auto p-6">
          {layouts.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Optimized Layouts</h2>
                {Object.keys(stats).length > 0 && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-accent-400">{stats.totalSheets}</span>
                      <span className="text-gray-500">Sheets</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-sage-600">{stats.efficiency}%</span>
                      <span className="text-gray-500">Efficiency</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-gray-700">{stats.placedPieces}/{stats.totalPieces}</span>
                      <span className="text-gray-500">Parts Placed</span>
                    </div>
                    {stats.unplacedPieces > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-semibold text-red-600">{stats.unplacedPieces}</span>
                        <span className="text-red-500">Unplaced</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {(() => {
                // Group layouts by thickness/stock type
                const groups = new Map();
                layouts.forEach(layout => {
                  const key = layout.thicknessLabel || layout.stockSheetTypeId || 'default';
                  if (!groups.has(key)) {
                    groups.set(key, { label: layout.thicknessLabel, layouts: [] });
                  }
                  groups.get(key).layouts.push(layout);
                });

                // Render all groups (even if single) with consistent hierarchy
                return Array.from(groups.entries()).map(([key, group]) => (
                  <div key={key} className="space-y-4">
                    {/* Stock type header - collapsible */}
                    <button
                      onClick={() => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                      className="flex items-center gap-3 w-full group cursor-pointer"
                    >
                      <ChevronLeft className={`w-4 h-4 text-gray-400 transition-transform ${collapsedGroups[key] ? 'rotate-180' : '-rotate-90'}`} />
                      <div className="h-px flex-1 bg-gray-300" />
                      <span className="text-sm font-semibold text-gray-500 tracking-wide px-2 group-hover:text-gray-700 transition-colors">
                        {group.label || 'Default Sheet'}
                      </span>
                      <div className="h-px flex-1 bg-gray-300" />
                    </button>
                    {!collapsedGroups[key] && (
                      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
                        {group.layouts.map((layout, idx) => (
                          <SheetCard
                            key={layout.id}
                            layout={layout}
                            title={`Sheet ${idx + 1} of ${group.layouts.length}`}
                            maxWidth={450}
                            maxHeight={280}
                            highlightedPieceId={highlightedPieceId}
                            onPieceHover={setHighlightedPieceId}
                            showStats={true}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <p className="text-lg">No layouts yet</p>
                <p className="text-sm">Add pieces and click Optimize to see layouts</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Drop Zone Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-accent-400 bg-opacity-10 border-4 border-dashed border-accent-400 z-40 pointer-events-none flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4 text-accent-500" />
            <p className="text-xl font-semibold text-gray-800">Drop file to import</p>
            <p className="text-sm text-gray-500 mt-2">CSV or 3D model (OBJ, GLTF, GLB)</p>
          </div>
        </div>
      )}

      {/* CSV Column Mapper Dialog */}
      <CSVColumnMapper
        isOpen={showColumnMapper}
        onClose={() => setShowColumnMapper(false)}
        onConfirm={processCSVWithMapping}
        headers={csvData.headers}
        sampleData={csvData.rows}
      />

      {/* 3D Model Importer Dialog */}
      <Model3DImporter
        isOpen={show3DImporter}
        onClose={() => {
          setShow3DImporter(false);
          setPending3DFile(null);
        }}
        onImport={handle3DImport}
        initialFile={pending3DFile}
        initialUnit={currentUnits.unit}
      />

      {/* Stock Sheet Type Modal */}
      {showStockTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingStockType ? 'Edit Sheet Type' : 'Add Sheet Type'}
              </h3>
              <button
                onClick={() => setShowStockTypeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Material Name</label>
                <input
                  type="text"
                  value={stockTypeForm.name}
                  onChange={(e) => setStockTypeForm({ ...stockTypeForm, name: e.target.value })}
                  placeholder={`Stock ${stockSheetTypes.length + 1}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Thickness ({getUnitLabel()})</label>
                <input
                  type="number"
                  step="0.01"
                  value={stockTypeForm.thickness}
                  onChange={(e) => setStockTypeForm({ ...stockTypeForm, thickness: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Width ({getUnitLabel()})</label>
                  <input
                    type="number"
                    value={stockTypeForm.width}
                    onChange={(e) => setStockTypeForm({ ...stockTypeForm, width: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Length ({getUnitLabel()})</label>
                  <input
                    type="number"
                    value={stockTypeForm.length}
                    onChange={(e) => setStockTypeForm({ ...stockTypeForm, length: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  />
                </div>
              </div>
              {parseFloat(stockTypeForm.width) > parseFloat(stockTypeForm.length) && parseFloat(stockTypeForm.length) > 0 && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-xs">
                    <p className="text-amber-800">
                      Width is larger than length. For best results, swap these values.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStockTypeForm({
                        ...stockTypeForm,
                        width: stockTypeForm.length,
                        length: stockTypeForm.width
                      })}
                      className="mt-1 font-medium text-amber-700 hover:text-amber-900 underline"
                    >
                      Swap width and length
                    </button>
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500">
                Common sizes:
              </div>
              <div className="flex flex-wrap gap-2">
                {currentUnits.system === 'imperial' ? (
                  <>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '48', length: '96' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">4×8 ft</button>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '60', length: '60' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">5×5 ft</button>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '48', length: '48' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">4×4 ft</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '122', length: '244' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">122×244 cm</button>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '125', length: '250' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">125×250 cm</button>
                    <button onClick={() => setStockTypeForm({ ...stockTypeForm, width: '122', length: '122' })} className="px-2 py-1 text-xs bg-sage-100 hover:bg-sage-200 rounded">122×122 cm</button>
                  </>
                )}
              </div>
              <button
                onClick={saveStockType}
                className="w-full py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-medium"
              >
                {editingStockType ? 'Save Changes' : 'Add Sheet Type'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Piece Modal */}
      {showAddPieceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Piece</h3>
              <button
                onClick={() => setShowAddPieceModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Name</label>
                <input
                  type="text"
                  value={pieceForm.name}
                  onChange={(e) => setPieceForm({ ...pieceForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Width ({getUnitLabel()})</label>
                  <input
                    type="number"
                    value={pieceForm.width}
                    onChange={(e) => {
                      const newWidth = e.target.value;
                      const pieceW = parseFloat(newWidth) || 0;
                      const pieceL = parseFloat(pieceForm.length) || 0;
                      const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(pieceForm.stockSheetTypeId);
                      const autoGrain = getAutoGrainDirection(pieceW, pieceL, sheetW, sheetL);
                      setPieceForm({ ...pieceForm, width: newWidth, grainDirection: autoGrain });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Length ({getUnitLabel()})</label>
                  <input
                    type="number"
                    value={pieceForm.length}
                    onChange={(e) => {
                      const newLength = e.target.value;
                      const pieceW = parseFloat(pieceForm.width) || 0;
                      const pieceL = parseFloat(newLength) || 0;
                      const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(pieceForm.stockSheetTypeId);
                      const autoGrain = getAutoGrainDirection(pieceW, pieceL, sheetW, sheetL);
                      setPieceForm({ ...pieceForm, length: newLength, grainDirection: autoGrain });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={pieceForm.quantity}
                    onChange={(e) => setPieceForm({ ...pieceForm, quantity: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Grain</label>
                  {(() => {
                    const pieceW = parseFloat(pieceForm.width) || 0;
                    const pieceL = parseFloat(pieceForm.length) || 0;
                    const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(pieceForm.stockSheetTypeId);
                    const validGrains = {
                      none: isGrainDirectionValid(pieceW, pieceL, 'none', sheetW, sheetL),
                      horizontal: isGrainDirectionValid(pieceW, pieceL, 'horizontal', sheetW, sheetL),
                      vertical: isGrainDirectionValid(pieceW, pieceL, 'vertical', sheetW, sheetL),
                    };
                    const cycleOrder = ['none', 'horizontal', 'vertical'];
                    const validCount = Object.values(validGrains).filter(Boolean).length;
                    const handleGrainClick = () => {
                      // If only one grain direction is valid, grain is locked
                      if (validCount === 1) {
                        setGrainWarning('Grain direction for this part is limited by sheet dimensions');
                        setTimeout(() => setGrainWarning(null), 3000);
                        return;
                      }
                      const currentIdx = cycleOrder.indexOf(pieceForm.grainDirection);
                      for (let i = 1; i <= 3; i++) {
                        const nextGrain = cycleOrder[(currentIdx + i) % 3];
                        if (validGrains[nextGrain]) {
                          setPieceForm({ ...pieceForm, grainDirection: nextGrain });
                          setGrainWarning(null);
                          return;
                        }
                      }
                      setGrainWarning('Part too large for any grain direction');
                      setTimeout(() => setGrainWarning(null), 3000);
                    };
                    return (
                      <>
                        <button
                          onClick={handleGrainClick}
                          title={pieceForm.grainDirection === 'horizontal' ? 'Along Length' : pieceForm.grainDirection === 'vertical' ? 'Along Width' : 'No Preference'}
                          className={`w-full px-3 py-2 rounded-md flex items-center justify-center gap-2 transition-colors cursor-pointer ${
                            !validGrains[pieceForm.grainDirection] ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:brightness-95'
                          }`}
                          style={validGrains[pieceForm.grainDirection] ? { backgroundColor: '#E5E7EB' } : {}}
                        >
                          {pieceForm.grainDirection === 'horizontal' ? <ArrowLeftRight className="w-5 h-5" /> : pieceForm.grainDirection === 'vertical' ? <ArrowUpDown className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                          <span className="text-sm">{pieceForm.grainDirection === 'horizontal' ? 'Along Length' : pieceForm.grainDirection === 'vertical' ? 'Along Width' : 'No Preference'}</span>
                        </button>
                        {!validGrains[pieceForm.grainDirection] && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Won't fit on sheet with this grain
                          </p>
                        )}
                        {grainWarning && validCount === 1 && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {grainWarning}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <label className="text-sm text-gray-600 block mb-1">Add to Sheet</label>
                <select
                  value={pieceForm.stockSheetTypeId || ''}
                  onChange={(e) => setPieceForm({ ...pieceForm, stockSheetTypeId: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-accent-400 focus:outline-none bg-white"
                >
                  <option value="">Unassigned (Default)</option>
                  {stockSheetTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({formatSheetSize(type.width, type.length)})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Thickness: <span className="font-medium">{formatThickness(getThicknessForSheet(pieceForm.stockSheetTypeId))}</span>
                </p>
              </div>
              <button
                onClick={saveNewPiece}
                className="w-full py-2.5 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-semibold"
              >
                Add Piece
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Execution Mode Modal */}
      {executionMode && (
        <ExecutionModeModal
          mode={executionMode}
          layouts={layouts}
          pieces={pieces}
          currentUnits={currentUnits}
          sheetWidth={sheetWidth}
          sheetLength={sheetLength}
          cutWidth={cutWidth}
          trimFactoryEdges={trimFactoryEdges}
          trimAmount={trimAmount}
          stats={stats}
          currentSheetIndex={currentSheetIndex}
          setCurrentSheetIndex={setCurrentSheetIndex}
          currentPieceIndex={currentPieceIndex}
          setCurrentPieceIndex={setCurrentPieceIndex}
          completedPieces={completedPieces}
          setCompletedPieces={setCompletedPieces}
          isLocked={isLocked}
          isSupported={isSupported}
          toggleWakeLock={toggleWakeLock}
          onClose={() => setExecutionMode(null)}
          getUnitLabel={getUnitLabel}
          showWatermark={showWatermark}
          modelSnapshot={modelSnapshot}
          stockSheetTypes={stockSheetTypes}
          isPro={isPro}
          license={license}
          promptUpgrade={promptUpgrade}
        />
      )}

      {/* Shop Sheet Preview Modal for Free Users */}
      {showPreviewModal && layouts.length > 0 && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#F4F7F5' }}>
          {/* Preview Banner - Fixed at top */}
          <div className="bg-[#D44A1F] px-6 py-3 flex items-center justify-center relative">
            <p className="text-sm text-white font-medium text-center">
              Preview Mode — Upgrade to unlock full functionality
            </p>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute right-4 p-1 text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Preview Content */}
          <div className="flex-1 overflow-auto p-8">
            <div className="filter blur-sm pointer-events-none opacity-75 max-w-3xl mx-auto grid gap-6">
              {/* 3D Model Overview */}
              {modelSnapshot && (
                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-600">3D Model Overview</h3>
                    <span className="text-xs text-gray-400">{layouts.length} sheet{layouts.length !== 1 ? 's' : ''} total</span>
                  </div>
                  <div className="flex justify-center" style={{ height: 300 }}>
                    {modelSnapshot.type === 'svg' ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: modelSnapshot.dataColor || modelSnapshot.data }}
                        style={{ lineHeight: 0, width: '100%', height: '100%' }}
                        className="[&>svg]:w-full [&>svg]:h-full [&>svg]:block"
                      />
                    ) : (
                      <img src={modelSnapshot.data} alt="3D Model" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
                    )}
                  </div>
                </div>
              )}
              {layouts.slice(0, 8).map((layout, index) => {
                const stockType = layout.stockTypeId
                  ? stockSheetTypes?.find(t => t.id === layout.stockTypeId)
                  : null;

                // Group pieces for parts table
                const layoutPieces = layout.cutSequence?.filter(cut => cut.type === 'piece') || layout.pieces || [];
                const groupedParts = [];
                const partMap = new Map();
                layoutPieces.forEach(item => {
                  const p = item.piece || item;
                  const key = `${p.name}|${p.width?.toFixed(4)}|${p.length?.toFixed(4)}`;
                  if (!partMap.has(key)) {
                    partMap.set(key, { num: groupedParts.length + 1, piece: p, qty: 0 });
                    groupedParts.push(partMap.get(key));
                  }
                  partMap.get(key).qty++;
                });
                const numToLetter = (n) => {
                  let s = '';
                  while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
                  return s;
                };

                return (
                  <div key={layout.id || index} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-500 font-medium">
                        Sheet {index + 1} of {layouts.length} {stockType ? `• ${stockType.name}` : ''}
                      </p>
                    </div>
                    <div className="flex justify-center mb-4">
                      <SheetRenderer
                        layout={layout}
                        mode="interactive"
                        maxWidth={560}
                        showLabels={true}
                        showDimensions={true}
                        numberLabels={true}
                      />
                    </div>
                    {/* Parts Table */}
                    {groupedParts.length > 0 && (
                      <div className="border-t border-gray-200 pt-3 mt-3">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-2 py-1 text-[10px] font-bold uppercase text-gray-500 w-12">Part</th>
                              <th className="px-2 py-1 text-[10px] font-bold uppercase text-gray-500 w-12">Qty</th>
                              <th className="px-2 py-1 text-[10px] font-bold uppercase text-gray-500 w-16">W</th>
                              <th className="px-2 py-1 text-[10px] font-bold uppercase text-gray-500 w-16">L</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedParts.slice(0, 6).map((part, idx) => {
                              const pieceColor = getPartColor(part.piece.originalId || part.piece.id).hex;
                              return (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                  <td className="px-2 py-1 font-medium">
                                    <span
                                      className="inline-flex items-center justify-center w-6 h-5 rounded-full text-[10px] font-bold"
                                      style={{
                                        backgroundColor: `${pieceColor}30`,
                                        color: pieceColor,
                                        border: `1.5px solid ${pieceColor}`
                                      }}
                                    >
                                      {numToLetter(part.num)}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1 text-gray-600">×{part.qty}</td>
                                  <td className="px-2 py-1 font-mono text-gray-700">{part.piece.width?.toFixed(1)}</td>
                                  <td className="px-2 py-1 font-mono text-gray-700">{part.piece.length?.toFixed(1)}</td>
                                </tr>
                              );
                            })}
                            {groupedParts.length > 6 && (
                              <tr>
                                <td colSpan={4} className="px-2 py-1 text-center text-gray-400 text-[10px]">
                                  +{groupedParts.length - 6} more parts...
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              {layouts.length > 8 && (
                <p className="text-center text-sm text-gray-400 py-2">
                  +{layouts.length - 8} more sheet{layouts.length - 8 > 1 ? 's' : ''}...
                </p>
              )}
            </div>
          </div>

          {/* Fixed Upgrade Overlay - Centered */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl mx-4 pointer-events-auto max-w-sm overflow-hidden">
              {/* Orange Header */}
              <div className="bg-[#D66A28] p-5 text-white">
                <div className="flex items-center justify-center gap-3">
                  <Sparkles className="w-8 h-8" />
                  <h3 className="text-xl font-bold">Pro Feature: Shop Sheet</h3>
                </div>
              </div>
              {/* Content */}
              <div className="p-6">
                <ul className="text-sm text-gray-600 space-y-3 mb-6 text-left">
                  <li className="flex items-start gap-3">
                    <ListChecks className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Interactive cut checklist</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Palette className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Color & print friendly formatting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Save className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Shareable project files</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Printer className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Print functionality</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    promptUpgrade('Upgrade to Pro for interactive cut checklists, shareable project files, and print capabilities.');
                  }}
                  className="w-full bg-[#D66A28] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#c25e23] transition-colors text-base"
                >
                  Upgrade to Pro
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="w-full mt-2 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Sheet Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title={`Delete "${deleteConfirm?.name}"?`}
        message="All parts assigned to this sheet will become unassigned and move to the default 96×48 in sheet."
        confirmLabel="Delete Sheet"
        onConfirm={() => {
          removeStockSheetType(deleteConfirm.id);
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Utility buttons moved to header */}
    </div>
  );
};

/**
 * Execution Mode Modal Component
 * Handles both guided cut mode and shop sheet/PDF view
 */
const ExecutionModeModal = ({
  mode,
  layouts,
  pieces = [],
  currentUnits,
  sheetWidth,
  sheetLength,
  cutWidth,
  trimFactoryEdges = false,
  trimAmount = 0,
  stats = {},
  currentSheetIndex,
  setCurrentSheetIndex,
  currentPieceIndex,
  setCurrentPieceIndex,
  completedPieces,
  setCompletedPieces,
  isLocked,
  isSupported,
  toggleWakeLock,
  onClose,
  getUnitLabel,
  showWatermark = false,
  modelSnapshot = null,
  stockSheetTypes = [],
  isPro = false,
  license = null,
  promptUpgrade = () => {},
}) => {
  const [printFriendly, setPrintFriendly] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(false);
  const [orientation, setOrientation] = React.useState('auto'); // 'auto', 'landscape', 'portrait'
  const [pdfExportStatus, setPdfExportStatus] = React.useState(null); // null | 'generating' | 'downloading' | 'complete' | 'error'
  const [cardSize, setCardSize] = React.useState({ width: 896, height: 600 });
  const containerRef = React.useRef(null);

  // Track which pieces have been cut (checked off) - for shop sheet interactivity
  const [cutPieceIds, setCutPieceIds] = React.useState(new Set());

  // Toggle a piece's cut status
  const togglePieceCut = (pieceId) => {
    setCutPieceIds(prev => {
      const next = new Set(prev);
      if (next.has(pieceId)) {
        next.delete(pieceId);
      } else {
        next.add(pieceId);
      }
      return next;
    });
  };

  // Toggle all pieces in a part group
  const togglePartGroupCut = (pieceIds) => {
    setCutPieceIds(prev => {
      const next = new Set(prev);
      const allCut = pieceIds.every(id => prev.has(id));
      if (allCut) {
        pieceIds.forEach(id => next.delete(id));
      } else {
        pieceIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Reset all cut marks
  const resetCutMarks = () => setCutPieceIds(new Set());

  // Calculate card size: height is primary constraint (fill viewport)
  // Width is derived from sheet aspect ratio, capped by window width
  React.useEffect(() => {
    const calculateSize = () => {
      const stickyHeaderHeight = 56;
      const containerPadding = 40; // 20px top + 20px bottom
      const windowPadding = 64; // horizontal padding from window edges

      // Max height = viewport minus sticky header and padding
      const maxHeight = window.innerHeight - stickyHeaderHeight - containerPadding;

      // Fixed card elements: header (~50px) + table area (~180px) + internal padding
      const cardHeaderHeight = 50;
      const cardTableHeight = 180;
      const cardPadding = 40;
      const fixedHeight = cardHeaderHeight + cardTableHeight + cardPadding;

      // Available height for SVG
      const svgHeight = maxHeight - fixedHeight;

      // Calculate SVG width based on sheet aspect ratio
      // SheetRenderer uses sheetLength as horizontal, sheetWidth as vertical
      const sheetAspect = sheetLength / sheetWidth;
      const svgWidth = svgHeight * sheetAspect;

      // Card width = SVG width + padding, capped by available window width
      const maxWindowWidth = window.innerWidth - windowPadding;
      const cardWidth = Math.min(svgWidth + cardPadding, maxWindowWidth);

      // If width-constrained, recalculate height based on the constrained width
      let finalHeight = maxHeight;
      if (svgWidth + cardPadding > maxWindowWidth) {
        const constrainedSvgWidth = maxWindowWidth - cardPadding;
        const constrainedSvgHeight = constrainedSvgWidth / sheetAspect;
        finalHeight = constrainedSvgHeight + fixedHeight;
      }

      setCardSize({
        width: Math.max(400, cardWidth),
        height: Math.max(400, finalHeight)
      });
    };

    calculateSize();
    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [sheetWidth, sheetLength]);

  // Get ordered layouts with optimized cut sequence
  const orderedLayouts = orderCutsForFabrication(layouts, cutWidth);

  // Snapshot layouts on mount for export (never affected by interactivity)
  const exportLayoutsRef = React.useRef(null);
  if (exportLayoutsRef.current === null) {
    exportLayoutsRef.current = orderedLayouts;
  }

  // Get flat list of all pieces in optimized order
  const allOrderedPieces = orderedLayouts.flatMap(layout =>
    layout.cutSequence
      .filter(cut => cut.type === 'piece')
      .map(cut => ({
        ...cut.piece,
        sheetNumber: layout.sheetNumber,
        cutNumber: cut.cutNumber,
        thicknessLabel: layout.thicknessLabel
      }))
  );

  const totalPieces = allOrderedPieces.length;
  const completedCount = completedPieces.size;
  const currentPiece = allOrderedPieces[currentPieceIndex];
  const currentLayout = orderedLayouts.find(l => l.sheetNumber === currentPiece?.sheetNumber);

  const markCompleteAndNext = () => {
    const pieceKey = `${currentPieceIndex}`;
    setCompletedPieces(new Set([...completedPieces, pieceKey]));

    // Move to next piece
    if (currentPieceIndex < totalPieces - 1) {
      setCurrentPieceIndex(currentPieceIndex + 1);
      // Update sheet index if needed
      const nextPiece = allOrderedPieces[currentPieceIndex + 1];
      if (nextPiece) {
        setCurrentSheetIndex(nextPiece.sheetNumber - 1);
      }
    }
  };

  const goToPiece = (index) => {
    setCurrentPieceIndex(index);
    const piece = allOrderedPieces[index];
    if (piece) {
      setCurrentSheetIndex(piece.sheetNumber - 1);
    }
  };

  const isComplete = completedCount === totalPieces;

  // Guided Cut Mode
  if (mode === 'guided') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-3">
              <ListChecks className="w-6 h-6 text-accent-400" />
              <h2 className="text-xl font-bold">Guided Cut Mode</h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {completedCount} / {totalPieces} cuts complete
              </span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-sage-500 rounded-full h-2 transition-all"
                  style={{ width: `${totalPieces > 0 ? (completedCount / totalPieces) * 100 : 0}%` }}
                />
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {isComplete ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-24 h-24 bg-sage-100 rounded-full flex items-center justify-center mb-6">
                <Check className="w-12 h-12 text-sage-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">All Done!</h2>
              <p className="text-gray-500 text-lg">You've completed all {totalPieces} cuts</p>
              <button
                onClick={onClose}
                className="mt-8 px-6 py-3 bg-accent-400 text-white rounded-lg font-semibold hover:bg-accent-500"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex-1 flex overflow-hidden">
              {/* Main View - Current Sheet */}
              <div className="flex-1 p-6 overflow-y-auto">
                {currentLayout && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-700">
                        Sheet {currentLayout.sheetNumber} of {orderedLayouts.length}
                        {currentLayout.thicknessLabel && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-sage-100 text-sage-700 rounded-full">
                            {currentLayout.thicknessLabel}
                          </span>
                        )}
                      </h3>
                    </div>
                    <div className="flex justify-center">
                      <SheetRenderer
                        layout={currentLayout}
                        mode="guided"
                        highlightedPieceId={currentPiece?.id || currentPiece?.originalId}
                        maxWidth={500}
                        maxHeight={350}
                        showLabels={true}
                      />
                    </div>
                  </div>
                )}

                {/* Current Piece Info */}
                {currentPiece && (
                  <div className="mt-6 bg-accent-50 rounded-xl p-6 border-2 border-accent-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-accent-500 font-medium mb-1">
                          Cut #{currentPieceIndex + 1} of {totalPieces}
                        </p>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {currentPiece.name || 'Piece'}
                        </h3>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-accent-600" title={`${formatPreciseDimension(currentPiece.width, currentUnits.unit, currentUnits.system)} × ${formatPreciseDimension(currentPiece.length, currentUnits.unit, currentUnits.system)}`}>
                          {formatDimension(currentPiece.width, currentUnits.unit, currentUnits.system, { includeUnit: false })} × {formatDimension(currentPiece.length, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                        </div>
                        <div className="text-sm text-gray-500">{getUnitLabel()}</div>
                      </div>
                    </div>
                    {(currentPiece.rotated || currentPiece.grainDirection !== 'none') && (
                      <div className="mt-4 flex gap-4 text-sm">
                        {currentPiece.rotated && (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full">
                            ↻ Rotated
                          </span>
                        )}
                        {currentPiece.grainDirection && currentPiece.grainDirection !== 'none' && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                            Grain: {currentPiece.grainDirection}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar - Cut List */}
              <div className="w-72 border-l bg-gray-50 flex flex-col">
                <div className="p-4 border-b bg-white">
                  <h3 className="font-semibold text-gray-700">Cut Sequence</h3>
                  <p className="text-xs text-gray-500 mt-1">Optimized for efficient fabrication</p>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {allOrderedPieces.map((piece, index) => {
                    const isCompleted = completedPieces.has(`${index}`);
                    const isCurrent = index === currentPieceIndex;

                    return (
                      <button
                        key={`${piece.id}-${index}`}
                        onClick={() => goToPiece(index)}
                        className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                          isCurrent
                            ? 'bg-accent-100 border-2 border-accent-400'
                            : isCompleted
                              ? 'bg-sage-50 border border-sage-200'
                              : 'bg-white border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCompleted ? 'bg-sage-500 text-white' : isCurrent ? 'bg-accent-400 text-white' : 'bg-gray-200'
                          }`}>
                            {isCompleted ? <Check className="w-3 h-3" /> : <span className="text-xs">{index + 1}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isCompleted ? 'text-gray-400 line-through' : ''}`}>
                              {piece.name || 'Piece'}
                            </p>
                            <p className="text-xs text-gray-400" title={`${formatPreciseDimension(piece.width, currentUnits.unit, currentUnits.system)} × ${formatPreciseDimension(piece.length, currentUnits.unit, currentUnits.system)}`}>
                              Sheet {piece.sheetNumber} • {formatDimension(piece.width, currentUnits.unit, currentUnits.system, { includeUnit: false })} × {formatDimension(piece.length, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Footer - Next Button */}
          {!isComplete && (
            <div className="p-4 border-t bg-white">
              <button
                onClick={markCompleteAndNext}
                className="w-full py-4 bg-sage-500 text-white rounded-xl font-bold text-lg hover:bg-sage-600 flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Mark Complete & Next
              </button>
            </div>
          )}
        </div>

      </div>
    );
  }

  // Shop Sheet / PDF Mode
  if (mode === 'shopsheet') {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: darkMode ? '#1a1a1a' : '#F4F7F5' }}>
        {/* Controls Header - Hidden when printing */}
        <div className="sticky top-0 px-6 py-3 flex items-center justify-between z-10 print:hidden" style={{ backgroundColor: darkMode ? '#111' : '#E8EFEB' }}>
          <button
            onClick={onClose}
            className={`flex items-center gap-2 font-medium ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Editor
          </button>
          <div className="flex items-center gap-3">
            {/* Color / Print Friendly Toggle */}
            <div className="flex items-center bg-white/60 rounded-full p-0.5">
              <button
                onClick={() => setPrintFriendly(false)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  !printFriendly ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Palette className="w-3.5 h-3.5" />
                Color
              </button>
              <button
                onClick={() => setPrintFriendly(true)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  printFriendly ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Printer className="w-3.5 h-3.5" />
                Print
              </button>
            </div>
            {/* Orientation Toggle - hidden, auto mode is default
            <div className="flex items-center bg-white/60 rounded-full p-0.5">
              <button
                onClick={() => setOrientation('auto')}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  orientation === 'auto' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Auto: landscape for wide sheets, portrait for square"
              >
                Auto
              </button>
              <button
                onClick={() => setOrientation('landscape')}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  orientation === 'landscape' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Landscape orientation"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setOrientation('portrait')}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  orientation === 'portrait' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Portrait orientation"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            </div>
            */}
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`relative w-11 h-6 rounded-full transition-colors ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all ${darkMode ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
            {isSupported && (
              <button
                onClick={toggleWakeLock}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  isLocked ? 'bg-yellow-400 text-gray-900' : 'bg-white/60 text-gray-500 hover:text-gray-700'
                }`}
              >
                <Sun className="w-3.5 h-3.5" />
                {isLocked ? 'Screen On' : 'Keep On'}
              </button>
            )}
            {cutPieceIds.size > 0 && (
              <button
                onClick={resetCutMarks}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/60 text-gray-500 hover:text-gray-700 hover:bg-white"
                title="Reset all cut marks"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
            )}
            {/* Save project button */}
            <button
              onClick={() => exportCutcalcFile({
                layouts: exportLayoutsRef.current,
                pieces,
                currentUnits,
                sheetWidth,
                sheetLength,
                cutWidth,
                trimFactoryEdges,
                trimAmount,
                stats,
                modelSnapshot,
                stockSheetTypes,
              })}
              className="flex items-center justify-center gap-1.5 px-3 h-8 bg-accent-400 hover:bg-accent-500 text-white rounded-lg text-xs font-semibold"
              title="Save project file"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            {/* Print button */}
            <button
              onClick={() => downloadShopSheet({
                orderedLayouts: exportLayoutsRef.current,
                currentUnits,
                sheetWidth,
                sheetLength,
                cutWidth,
                getUnitLabel,
                showWatermark,
                printFriendly,
                modelSnapshot,
                orientation,
              })}
              className="flex items-center justify-center gap-1.5 px-3 h-8 bg-accent-400 hover:bg-accent-500 text-white rounded-lg text-xs font-semibold"
              title="Print"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>

        {/* Shop Sheets */}
        <div ref={containerRef} className="px-8 space-y-10 flex flex-col items-center" style={{ paddingTop: 20, paddingBottom: 20 }}>
          {/* 3D Model Overview */}
          {modelSnapshot && (
            <div className={`rounded-xl border overflow-hidden flex flex-col ${darkMode ? 'bg-[#1e1e1e] border-[#333]' : 'bg-white border-gray-200'}`} style={{ width: cardSize.width, height: cardSize.height }}>
              <div className="flex items-center justify-between px-4 py-2 flex-shrink-0">
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>3D Model Overview</h3>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{orderedLayouts.length} sheet{orderedLayouts.length !== 1 ? 's' : ''} total</span>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden" style={{ padding: 8 }}>
                {modelSnapshot.type === 'svg' ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: printFriendly ? (modelSnapshot.dataBW || modelSnapshot.data) : (modelSnapshot.dataColor || modelSnapshot.data) }}
                    style={{ lineHeight: 0, width: '100%', height: '100%' }}
                    className="[&>svg]:w-full [&>svg]:h-full [&>svg]:block"
                  />
                ) : (
                  <img src={modelSnapshot.data} alt="3D Model" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', ...(printFriendly ? { filter: 'grayscale(1)' } : {}) }} />
                )}
              </div>
            </div>
          )}
          {orderedLayouts.map((layout, sheetIndex) => {
            const pieces = layout.cutSequence.filter(cut => cut.type === 'piece');
            const grainLabels = {
              horizontal: '↔ Length',
              vertical: '↕ Width',
              none: '○ None',
            };

            // Group pieces by name+dimensions+grain into unique parts with qty
            const groupedParts = [];
            const partMap = new Map();
            pieces.forEach(cut => {
              const p = cut.piece;
              const key = `${p.name}|${p.width.toFixed(4)}|${p.length.toFixed(4)}|${p.grainDirection}`;
              if (!partMap.has(key)) {
                const partNum = groupedParts.length + 1;
                const entry = { num: partNum, piece: p, qty: 0, pieceIds: [] };
                partMap.set(key, entry);
                groupedParts.push(entry);
              }
              const entry = partMap.get(key);
              entry.qty++;
              if (p.id) {
                entry.pieceIds.push(p.id);
              }
            });

            const half = Math.ceil(groupedParts.length / 2);
            const col1 = groupedParts.slice(0, half);
            const col2 = groupedParts.slice(half);

            const numToLetter = (n) => {
              let s = '';
              while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
              return s;
            };

            const renderTable = (parts) => (
              <table className="w-full text-left">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-[#444]' : 'border-gray-300'}`}>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider w-8 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}></th>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Part</th>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider w-10 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Qty</th>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider w-14 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>W</th>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider w-14 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>L</th>
                    <th className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider w-20 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Grain</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((part, idx) => {
                    const grain = grainLabels[part.piece.grainDirection] || grainLabels.none;
                    const pieceColor = !printFriendly ? getPartColor(part.piece.originalId || part.piece.id).hex : null;
                    const allCut = part.pieceIds.length > 0 && part.pieceIds.every(id => cutPieceIds.has(id));
                    const someCut = part.pieceIds.some(id => cutPieceIds.has(id));
                    const cutCount = part.pieceIds.filter(id => cutPieceIds.has(id)).length;
                    return (
                      <tr
                        key={idx}
                        onClick={() => togglePartGroupCut(part.pieceIds)}
                        className={`cursor-pointer hover:bg-gray-100 transition-colors ${idx % 2 === 0 ? (darkMode ? 'bg-transparent' : 'bg-white') : (darkMode ? 'bg-white/5' : 'bg-gray-50/50')} ${allCut ? 'opacity-50' : ''}`}
                      >
                        <td className={`px-2 py-1.5 text-xs font-medium border-b ${darkMode ? 'border-[#333]' : 'border-gray-100'}`}>
                          <span
                            className={`inline-flex items-center justify-center w-6 h-5 rounded-full text-[10px] font-bold ${allCut ? 'line-through' : ''}`}
                            style={pieceColor
                              ? { backgroundColor: `${pieceColor}30`, color: pieceColor, border: `1.5px solid ${pieceColor}` }
                              : { backgroundColor: 'transparent', color: darkMode ? '#ccc' : '#333', border: `1.5px solid ${darkMode ? '#ccc' : '#333'}` }
                            }
                          >
                            {numToLetter(part.num)}
                          </span>
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-semibold border-b truncate max-w-0 ${darkMode ? 'text-gray-200 border-[#333]' : 'text-gray-800 border-gray-100'} ${allCut ? 'line-through' : ''}`}>
                          {part.piece.name || 'Piece'}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-medium border-b ${darkMode ? 'text-gray-400 border-[#333]' : 'text-gray-600 border-gray-100'}`}>
                          {someCut && !allCut ? (
                            <span>{cutCount}<span className="text-gray-400">/{part.qty}</span></span>
                          ) : (
                            <span className={allCut ? 'line-through' : ''}>×{part.qty}</span>
                          )}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono border-b ${darkMode ? "text-gray-300 border-[#333]" : "text-gray-700 border-gray-100"} ${allCut ? 'line-through' : ''}`} title={formatPreciseDimension(part.piece.width, currentUnits.unit, currentUnits.system)}>
                          {formatDimension(part.piece.width, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono border-b ${darkMode ? "text-gray-300 border-[#333]" : "text-gray-700 border-gray-100"} ${allCut ? 'line-through' : ''}`} title={formatPreciseDimension(part.piece.length, currentUnits.unit, currentUnits.system)}>
                          {formatDimension(part.piece.length, currentUnits.unit, currentUnits.system, { includeUnit: false })}
                        </td>
                        <td className={`px-2 py-1.5 text-xs border-b whitespace-nowrap ${darkMode ? 'text-gray-400 border-[#333]' : 'text-gray-500 border-gray-100'} ${allCut ? 'line-through' : ''}`}>
                          {grain}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );

            return (
              <div key={layout.id} className={`rounded-lg shadow-sm flex flex-col overflow-hidden ${darkMode ? 'bg-[#242424] border border-[#333]' : 'bg-white border border-gray-200'}`} style={{ width: cardSize.width, height: cardSize.height }}>
                {/* Header — single row */}
                <div className={`flex justify-between items-baseline px-5 py-3 border-b ${darkMode ? 'border-[#333]' : 'border-gray-200'}`}>
                  <h2 className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sheet {sheetIndex + 1} <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>of {orderedLayouts.length}</span>
                  </h2>
                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} title={`${formatPreciseDimension(sheetWidth, currentUnits.unit, currentUnits.system)} × ${formatPreciseDimension(sheetLength, currentUnits.unit, currentUnits.system)}`}>
                    {layout.thicknessLabel ? layout.thicknessLabel + ' · ' : ''}
                    {formatDimension(sheetWidth, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true })} × {formatDimension(sheetLength, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true })} {getUnitLabel()}
                  </span>
                </div>

                {/* SVG — fills available space */}
                <div className="shop-sheet-svg-container flex-1 min-h-0 flex items-center justify-center p-5 overflow-hidden">
                  <SheetRenderer
                    layout={layout}
                    mode="interactive"
                    highContrast={printFriendly}
                    darkMode={darkMode}
                    maxWidth={cardSize.width - 40}
                    maxHeight={cardSize.height - 230}
                    showLabels={true}
                    showDimensions={true}
                    numberLabels={true}
                    cutSequence={layout.cutSequence}
                    cutPieceIds={cutPieceIds}
                    onPieceClick={(piece) => togglePieceCut(piece.id)}
                    fontScale={1.5}
                  />
                </div>

                {/* Two-column table — pinned bottom */}
                <div className="px-5 pb-4 mt-auto">
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0">{renderTable(col1)}</div>
                    {col2.length > 0 && <div className="flex-1 min-w-0">{renderTable(col2)}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          {showWatermark ? (
            <div className="space-y-1">
              <div className="text-base font-semibold text-gray-300">FREE VERSION — CUTCALC.COM</div>
              <div className="text-xs text-gray-400">Upgrade to Pro to remove watermark</div>
            </div>
          ) : (
            <div className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Generated by CutCalc • {new Date().toLocaleDateString()}</div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default DesktopApp;
